import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";

export interface KpiLog {
  id: string;
  kpiId: string;
  occurredOn: string;
  label: string;
  note: string;
  amount: number | null;
}

const LS_KEY = "bb_kpi_logs";
const supabaseReady = () => isSupabaseConfigured && !!supabase;

function missingTable(error: unknown): boolean {
  const value = error as { code?: string; message?: string } | null;
  return (
    value?.code === "42P01" ||
    value?.code === "PGRST205" ||
    Boolean(value?.message?.includes("does not exist"))
  );
}

function localRead(): KpiLog[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? "[]") as KpiLog[];
  } catch {
    return [];
  }
}

function localWrite(rows: KpiLog[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(rows));
}

interface LogRow {
  id: string;
  kpi_id: string;
  occurred_on: string;
  label: string;
  note: string;
  amount: number | string | null;
}

function rowToLog(row: LogRow): KpiLog {
  return {
    id: row.id,
    kpiId: row.kpi_id,
    occurredOn: row.occurred_on,
    label: row.label ?? "",
    note: row.note ?? "",
    amount: row.amount === null || row.amount === undefined ? null : Number(row.amount),
  };
}

export async function listKpiLogs(kpiId: string, start: string, endExclusive: string): Promise<KpiLog[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_logs")
      .select("*")
      .eq("kpi_id", kpiId)
      .gte("occurred_on", start)
      .lt("occurred_on", endExclusive)
      .order("occurred_on", { ascending: false });
    if (!error) return (data as LogRow[]).map(rowToLog);
    if (!missingTable(error)) throw error;
  }
  return localRead().filter(
    (row) => row.kpiId === kpiId && row.occurredOn >= start && row.occurredOn < endExclusive,
  );
}

export async function addKpiLog(
  log: Omit<KpiLog, "id">,
  userId?: string,
): Promise<KpiLog> {
  const record: KpiLog = { ...log, id: crypto.randomUUID() };
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_logs")
      .insert({
        id: record.id,
        kpi_id: record.kpiId,
        occurred_on: record.occurredOn,
        label: record.label,
        note: record.note,
        amount: record.amount,
        created_by: userId ?? null,
      })
      .select("*")
      .single();
    if (!error) return rowToLog(data as LogRow);
    if (!missingTable(error)) throw error;
  }
  const all = localRead();
  all.unshift(record);
  localWrite(all);
  return record;
}

export async function deleteKpiLog(id: string): Promise<void> {
  if (supabaseReady()) {
    const { error } = await supabase!.from("kpi_logs").delete().eq("id", id);
    if (!error) return;
    if (!missingTable(error)) throw error;
  }
  localWrite(localRead().filter((row) => row.id !== id));
}

/** Count of log rows (new customers) or sum of amounts (pipeline). Rates stay typed in. */
export function derivedValueFromLogs(kpiId: string, logs: KpiLog[]): number | null {
  if (kpiId === "new-direct-customers") return logs.length;
  if (kpiId === "pipeline-value") {
    return logs.reduce((sum, log) => sum + (typeof log.amount === "number" && Number.isFinite(log.amount) ? log.amount : 0), 0);
  }
  return null;
}
